import { useRef, useEffect, useState } from "react";

import { newRelic } from "utils/logUtils";

import usePrevious from "./usePrevious";

const useVideoJsPlayer = ({ src, autoplay, className, ...props }) => {
  const vjsId = `vjs-${Date.now()}`;
  const vjsRef = useRef(null);
  const videojs = useRef(null);

  const [isVideojsLoaded, setIsVideojsLoaded] = useState(false);
  let player;

  const previousSrc = usePrevious(src);
  const previousAutoplay = usePrevious(autoplay);

  const initPlayer = () => {
    player = videojs.current(vjsRef.current, props);
    player.src({
      src,
      type: "video/mp4",
    });
    player.autoplay(autoplay);
  };

  const resetPlayer = () => {
    player = videojs.current(vjsRef.current);
    player.currentTime(0);
    player.pause();
  };

  const changeSrc = () => {
    player = videojs.current(vjsRef.current);

    if (!player.paused()) {
      resetPlayer();
    }

    player.src({
      src,
      type: "video/mp4",
    });

    player.poster(props.poster);
  };

  const changeAutoplay = () => {
    player = videojs.current(vjsRef.current);
    player.autoplay(autoplay);

    if (autoplay) {
      player.play();
    } else {
      player.pause();
    }
  };

  const exitFullscreen = () => {
    player = videojs.current(vjsRef.current);

    if (player.isFullscreen()) {
      player.exitFullscreen();
    }
  };

  const initPlayerEvents = () => {
    player.ready(() => {
      window.p = player;
    });

    player.on("error", () => {
      const error = player.error();
      newRelic(`VideoJS Error: ${error.message}`, error);
    });
  };

  const unregisterEvents = () => {
    player.off("error");
  };

  useEffect(() => {
    import("video.js")
      .then((videojsPlayer) => {
        setIsVideojsLoaded(true);
        videojs.current = videojsPlayer.default;
      })
      .then(() => {
        if (previousSrc !== src && !previousSrc) {
          initPlayer();
          initPlayerEvents();
        }
        if (previousSrc !== src && previousSrc) {
          changeSrc();
          unregisterEvents();
          initPlayerEvents();
        }

        if (previousAutoplay !== autoplay && previousAutoplay !== undefined) {
          changeAutoplay();
          unregisterEvents();
          initPlayerEvents();
        }
      });
  }, [src, autoplay]);

  const videoClassNames = `video-js ${className} ${props.bigPlayButtonCentered ? "vjs-big-play-centered" : ""}`;

  return { vjsRef, vjsId, vjsClassName: videoClassNames, exitFullscreen, resetPlayer, isVideojsLoaded };
};

export default useVideoJsPlayer;
